import { useState } from "react";
import axios from "axios";
import { Dialog, DialogContent } from "./ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "./ui/alert-dialog";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import {
  FileSignature, Trash2, Check, X, Clock, Loader2, Mail, MessageCircle, Send, RefreshCw, Download,
} from "lucide-react";
import { useAppearance } from "../contexts/AppearanceContext";
import { applyMask } from "./PhoneInput";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_MAP = {
  pending: { label: "Pendente", bg: "bg-amber-100 text-amber-700" },
  signed: { label: "Assinado", bg: "bg-emerald-100 text-emerald-700" },
  rejected: { label: "Recusado", bg: "bg-rose-100 text-rose-700" },
  revoked: { label: "Revogado", bg: "bg-slate-200 text-slate-600" },
  deleted: { label: "Excluído", bg: "bg-slate-200 text-slate-600" },
};

export default function ContractDetailModal({ contract, open, onClose, onRefresh }) {
  const appearance = useAppearance();
  const phoneMask = appearance.phone_mask || "(##) #####-####";
  const tc500 = appearance.colors?.["500"] || "#F97316";
  const tc50 = appearance.colors?.["50"] || "#FFF7ED";

  const [revokeOpen, setRevokeOpen] = useState(false);
  const [resendOpen, setResendOpen] = useState(false);
  const [resendHistory, setResendHistory] = useState([]);
  const [resendLoading, setResendLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [localContract, setLocalContract] = useState(null);
  const [downloading, setDownloading] = useState(false);

  const c = localContract || contract;
  if (!c) return null;
  const st = STATUS_MAP[c.status] || STATUS_MAP.pending;

  const handleDownload = async () => {
    // If we already have the URL, open it
    if (c.signed_file_url) {
      window.open(c.signed_file_url, "_blank");
      return;
    }
    // Otherwise refresh to get it
    setDownloading(true);
    try {
      const r = await axios.post(`${API}/autentique/contracts/${c.id}/refresh-status`);
      if (r.data.ok && r.data.signed_file_url) {
        setLocalContract(prev => ({ ...(prev || c), signed_file_url: r.data.signed_file_url, status: r.data.status, signatures: r.data.signatures }));
        window.open(r.data.signed_file_url, "_blank");
      } else {
        toast.error("URL do documento ainda não disponível. Tente novamente.");
      }
    } catch { toast.error("Erro ao buscar documento."); }
    setDownloading(false);
  };

  const handleRefreshStatus = async () => {
    try {
      const r = await axios.post(`${API}/autentique/contracts/${c.id}/refresh-status`);
      if (r.data.ok) {
        setLocalContract(prev => ({ ...(prev || c), status: r.data.status, signatures: r.data.signatures, signed_file_url: r.data.signed_file_url || (prev || c).signed_file_url }));
        if (onRefresh) onRefresh();
        toast.success("Atualizado!");
      }
    } catch { toast.error("Erro"); }
  };

  const openResend = async () => {
    setResendLoading(true);
    setResendOpen(true);
    try {
      const r = await axios.get(`${API}/autentique/contracts/${c.id}/resend-history`);
      setResendHistory(r.data || []);
    } catch { setResendHistory([]); }
    setResendLoading(false);
  };

  const handleResend = async () => {
    setResending(true);
    try {
      await axios.post(`${API}/autentique/contracts/${c.id}/resend`);
      toast.success("Contrato reenviado!");
      setResendOpen(false);
      if (onRefresh) onRefresh();
    } catch { toast.error("Erro ao reenviar"); }
    setResending(false);
  };

  const handleRevoke = async () => {
    try {
      await axios.delete(`${API}/autentique/contracts/${c.id}`);
      toast.success("Contrato excluído!");
      setRevokeOpen(false);
      onClose();
      if (onRefresh) onRefresh();
    } catch { toast.error("Erro"); }
  };

  return (<>
    <Dialog open={open} onOpenChange={v => { if (!v) { setLocalContract(null); onClose(); } }}>
      <DialogContent className="max-w-lg !p-0 overflow-hidden" data-testid="contract-detail-modal">
        <div className="px-6 pt-5 pb-1">
          <p className="font-heading font-bold text-lg text-slate-900 flex items-center gap-2">
            <FileSignature className="w-5 h-5" style={{ color: tc500 }} />{c.doc_name}
          </p>
        </div>
        <div className="px-6 pb-6 space-y-3">
          {/* Deal card */}
          <div className="flex items-center gap-3 p-3 bg-white border border-slate-200 rounded-lg">
            <div className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: tc50 }}>
              <FileSignature className="w-4 h-4" style={{ color: tc500 }} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-slate-800">{c.negocio_titulo || c.doc_name || "—"}</p>
              <p className="text-[11px] text-slate-400">
                {c.contato_nome || ""}{c.contato_nome && c.empresa_nome ? " · " : ""}{c.empresa_nome || ""}
              </p>
            </div>
            <Badge className={`${st.bg} border-0 text-[9px]`}>{st.label}</Badge>
          </div>

          {/* Info */}
          <div className="flex items-center gap-2 text-[11px] text-slate-400">
            <span>{c.source === "manual_upload" ? "Anexado" : "Enviado"} em {new Date(c.created_at).toLocaleDateString("pt-BR")} às {new Date(c.created_at).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}</span>
            <span>·</span>
            <span>{c.created_by_name}</span>
            {c.source === "manual_upload" && <><span>·</span><span className="text-blue-500">Anexado manualmente</span></>}
          </div>

          {/* Manual upload — download button */}
          {c.source === "manual_upload" && c.file_id && (
            <Button variant="outline" size="sm" className="w-full gap-2 h-9" onClick={() => window.open(`${API}/files/download/${c.file_id}`, "_blank")}>
              <Download className="w-3.5 h-3.5" />Visualizar / Baixar arquivo
            </Button>
          )}

          {/* Signers — only show for Autentique contracts */}
          {c.source !== "manual_upload" && (<>
            <div>
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Signatários</p>
            <div className="space-y-1.5">
              {(c.signatures || []).filter(sig => {
                // If signers_input exists, only show signatures that match a known signer
                if (!c.signers_input || c.signers_input.length === 0) return true;
                const sigEmail = (sig.email || "").toLowerCase();
                const sigName = (sig.name || "").toLowerCase();
                return c.signers_input.some(si =>
                  (si.email && si.email.toLowerCase() === sigEmail) ||
                  (si.name && sigName && si.name.toLowerCase() === sigName) ||
                  (si.phone && !sigEmail)
                );
              }).map((sig, i) => {
                const signed = sig.signed?.created_at;
                const rejected = sig.rejected?.created_at;
                const ourSigner = c.signers_input?.find(si =>
                  (si.email && si.email === sig.email) ||
                  (si.name && (sig.name || "").toLowerCase().includes(si.name.toLowerCase())) ||
                  (si.phone && !sig.email)
                );
                const isWhatsapp = !!(ourSigner?.phone) || sig.delivery_method === "DELIVERY_METHOD_WHATSAPP";
                const isTemplate = ourSigner?._source === "template";
                const displayPhone = ourSigner?.phone || sig.phone;
                const link = sig.link?.short_link;
                const displayName = sig.name || (isWhatsapp ? applyMask((displayPhone || "").replace(/\D/g, "").replace(/^55/, ""), phoneMask) : sig.email) || "Signatário";
                const displayEmail = sig.name ? (isWhatsapp ? applyMask((displayPhone || "").replace(/\D/g, "").replace(/^55/, ""), phoneMask) || sig.email : sig.email) : "";

                return (
                  <div key={i} className="flex items-center gap-3 p-2.5 bg-white border border-slate-200 rounded-lg">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${signed ? "bg-emerald-50" : rejected ? "bg-rose-50" : isWhatsapp ? "bg-emerald-50" : "bg-blue-50"}`}>
                      {signed ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : rejected ? <X className="w-3.5 h-3.5 text-rose-500" /> : isWhatsapp ? <MessageCircle className="w-3.5 h-3.5 text-emerald-500" /> : <Mail className="w-3.5 h-3.5 text-blue-500" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-800">{displayName}</p>
                      <p className="text-[11px] text-slate-400">{displayEmail}</p>
                    </div>
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      {isTemplate && <Badge className="border-0 text-[9px] bg-slate-100 text-slate-500">Padrão</Badge>}
                      <Badge className={`border-0 text-[9px] ${signed ? "bg-emerald-100 text-emerald-700" : rejected ? "bg-rose-100 text-rose-700" : isWhatsapp ? "bg-emerald-100 text-emerald-700" : "bg-blue-100 text-blue-700"}`}>
                        {signed ? "Assinado" : rejected ? "Recusado" : isWhatsapp ? "WhatsApp" : "E-mail"}
                      </Badge>
                      {link && <a href={link} target="_blank" rel="noreferrer" className="text-[10px] text-blue-500 hover:underline">Link</a>}
                    </div>
                  </div>
                );
              })}
            </div>
            </div>
          </>)}

          {/* Actions */}
          {c.status !== "revoked" && c.status !== "deleted" && (
          <div className="flex items-center gap-2 pt-2">
            <Button size="sm" className="h-8 w-8 p-0 bg-rose-600 hover:bg-rose-700 text-white border-0" onClick={() => setRevokeOpen(true)} title="Excluir">
              <Trash2 className="w-3.5 h-3.5" />
            </Button>
            <div className="flex-1" />
            {c.source !== "manual_upload" && c.status !== "signed" && (
              <Button variant="outline" size="sm" className="h-8 w-8 p-0 bg-white text-slate-500 border-slate-300 hover:bg-slate-50 hover:text-slate-700" onClick={handleRefreshStatus} title="Atualizar">
                <RefreshCw className="w-3.5 h-3.5" />
              </Button>
            )}
            {c.source !== "manual_upload" && c.status !== "signed" && (
              <Button size="sm" className="h-8 px-3 text-xs gap-1.5 text-white" style={{ backgroundColor: tc500 }} onClick={openResend} title="Reenviar">
                <Send className="w-3.5 h-3.5" />Reenviar
              </Button>
            )}
            {c.status === "signed" && (
              <Button size="sm" className="text-xs gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white" onClick={handleDownload} disabled={downloading}>
                {downloading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />}Download
              </Button>
            )}
          </div>
          )}
        </div>
      </DialogContent>
    </Dialog>

    {/* Revoke */}
    <AlertDialog open={revokeOpen} onOpenChange={setRevokeOpen}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2 text-rose-600"><Trash2 className="w-5 h-5" />Excluir contrato?</AlertDialogTitle>
          <AlertDialogDescription>A assinatura será bloqueada e o documento será excluído da Autentique. Esta ação não pode ser desfeita.</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancelar</AlertDialogCancel>
          <AlertDialogAction className="bg-rose-600 hover:bg-rose-700 text-white" onClick={handleRevoke}>Excluir</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>

    {/* Resend with History */}
    <Dialog open={resendOpen} onOpenChange={setResendOpen}>
      <DialogContent className="max-w-md">
        <div className="space-y-3 pt-4">
          {resendLoading ? (
            <div className="flex justify-center py-4"><Loader2 className="w-5 h-5 animate-spin" style={{ color: tc500 }} /></div>
          ) : (<>
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Histórico de envios</p>
            <div className="space-y-1.5 max-h-48 overflow-y-auto">
              {resendHistory.map((h, i) => (
                <div key={i} className="flex items-center gap-3 p-2.5 bg-white border border-slate-200 rounded-lg">
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${h.is_original ? "bg-indigo-50" : "bg-slate-100"}`}>
                    <Send className={`w-3 h-3 ${h.is_original ? "text-indigo-500" : "text-slate-400"}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-slate-700">{h.is_original ? "Envio original" : "Reenvio"}</p>
                    <p className="text-[10px] text-slate-400">
                      {new Date(h.sent_at).toLocaleDateString("pt-BR")} às {new Date(h.sent_at).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })} · {h.sent_by}
                    </p>
                  </div>
                </div>
              ))}
            </div>
            <p className="text-xs text-slate-500">Deseja realmente reenviar este contrato para assinatura?</p>
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1" onClick={() => setResendOpen(false)}>Cancelar</Button>
              <Button className="flex-1 text-white" style={{ backgroundColor: tc500 }} disabled={resending} onClick={handleResend}>
                {resending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}Reenviar
              </Button>
            </div>
          </>)}
        </div>
      </DialogContent>
    </Dialog>
  </>);
}
